import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useGetAssociationMembers } from "../queries/association-member";
import { formatUserName } from "../services/board-meeting";
import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";

export type BoardMeetingFormValues = {
  name: string;
  // Value of a datetime-local input
  date: string;
  location: string;
  calledIds: string[];
};

export function BoardMeetingForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: BoardMeetingFormValues) => void;
  onSubmit: () => void;
  values: BoardMeetingFormValues;
  loading?: boolean;
  hasError?: boolean;
}) {
  const { locale: t } = useLocale();
  const { data: members } = useGetAssociationMembers();

  const activeMembers = useMemo(
    () =>
      (members ?? [])
        .filter((member) => member.active)
        .sort((left, right) =>
          formatUserName(left).localeCompare(formatUserName(right)),
        ),
    [members],
  );

  const isValid = values.name.trim() !== "" && values.date !== "";

  const toggleCalled = (memberId: string) => {
    onChange({
      ...values,
      calledIds: values.calledIds.includes(memberId)
        ? values.calledIds.filter((id) => id !== memberId)
        : [...values.calledIds, memberId],
    });
  };

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          className="col-span-2"
          disabled={loading}
          label={t.boardMeeting.name}
          type="text"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
        />
        <Input
          required
          disabled={loading}
          label={t.boardMeeting.date}
          type="datetime-local"
          value={values.date}
          onChange={(e) => onChange({ ...values, date: e.target.value })}
        />
        <Input
          disabled={loading}
          label={t.boardMeeting.location}
          type="text"
          value={values.location}
          onChange={(e) => onChange({ ...values, location: e.target.value })}
        />
        <fieldset className="col-span-2 border-t border-gray-200 mt-2 pt-3">
          <legend className="block text-sm font-medium text-gray-500">
            {t.boardMeeting.called}
          </legend>
          <p className="text-xs text-gray-500 mb-2">
            {t.boardMeeting.calledHint}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-60 overflow-y-auto border-1 border-gray-300 rounded-xs p-2 bg-white">
            {activeMembers.map((member) => (
              <Checkbox
                key={member.id}
                checked={values.calledIds.includes(member.id)}
                className="flex items-center gap-2 text-sm"
                disabled={loading}
                label={formatUserName(member)}
                onClick={() => toggleCalled(member.id)}
              />
            ))}
          </div>
        </fieldset>
      </div>
      <div className="flex gap-2 justify-end mt-4">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.boardMeeting.error}
          </p>
        )}
        <Button disabled={loading} severity="secondary" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={!isValid} loading={loading} type="submit">
          {t.page.save}
        </Button>
      </div>
    </form>
  );
}
